import {Injectable} from "@angular/core";
import {ClientConfiguration} from "./client.config";
import {empty, interval, Observable, Observer, Subscription} from "rxjs";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import "rxjs/add/operator/share";
import "rxjs/add/observable/interval";
import "rxjs/add/observable/of";
import {PersistenceService} from "../persistence/persistence.service";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {EventBus} from "../../event/EventBus";
import {UploadService} from "../interaction/upload.service";
import {ILibrarySelection} from "../../library/library.objects";
import {BookSessionType, ErrorType, Scilearn} from "../../constants";
import {ModalConfig} from "../notification/modal.config";
import {SESSION_STATUS, SessionEvent} from "../../event/SessionEvent";
import {Login} from "../../login/login";
import {BrowserAgent} from "../browser/browser.agent";
import {EXIT_STATUS, ExitEvent} from "../../event/ExitEvent";
import {flatMap} from "rxjs/operators";
import {of} from "rxjs/internal/observable/of";
import {forkJoin} from "rxjs/internal/observable/forkJoin";
import {UserModelService} from "../usermodel/usermodel.service";

class BookSessionResult {
    BookSessionId:string;
    Description:string;
}

@Injectable()
export class SessionService {

    public static MAX_RETRIES = 5;

    protected _bookSessionId:string;
    protected _partType:BookSessionType;
    protected _partId:string;
    protected _currentRetries:number = 0;
    protected _completedGesture:boolean = false;
    private browserAgent: BrowserAgent;

    // TODO: Can be removed once slc backend removes requirement for part_id
    // no longer needed feature, part of legacy RAEE architecture
    private DEFAULT_PART_ID: string = "-1";
    private exitProcessTriggered: boolean = false;
    public keepAliveSubscription:Subscription;

  constructor(private config: ClientConfiguration,
              private login: Login,
              private uploadService: UploadService,
              private persistenceService: PersistenceService,
              private log: BugsnagLoggingService,
              private eventBus: EventBus,
              private modalConfig: ModalConfig,
              private userModelService: UserModelService
                ) {
        this.browserAgent = new BrowserAgent();
    }

    public init():Observable<string> {
        return new Observable((observer:Observer<any>) => {
            this.persistenceService.getItem(Scilearn.BOOKSESSION_KEY).subscribe(
                (value:string) => {
                    if (value) {
                        this._bookSessionId = value;
                    }
                    this.persistenceService.removeItem(Scilearn.BOOKSESSION_KEY).subscribe(
                        () => this.startTrainingSession().subscribe(
                            () => {
                              this.setKeepAlive();
                                observer.next("OK");
                                observer.complete();
                                this.log.log("init completed");
                            })
                    );
                }
            );
        });
    }

    public startTrainingSession():Observable<Object> {
        let sessionData = {
            learnerId: this.login.learnerId,
            httpSessionId: this.login.sessionId
        };

        let modifySession = this.uploadService.startTrainingSession(sessionData,
            this.config.trainingPath);

        modifySession.subscribe(() => {
            this.log.log("started training session");
            this.persistenceService.setItem("trainingSessionId", this.login.sessionId).subscribe();
            this.persistenceService.setItem("learnerId", this.login.learnerId).subscribe();
            this.eventBus.next(new SessionEvent(SESSION_STATUS.start_trainingsession));
        });

        return modifySession;
    }

    public closeTrainingSession(synchronous?: boolean):Observable<Object> {
        let modifySession;
        modifySession = this.uploadService.closeTrainingSession(
                this.config.trainingPath + this.login.sessionId, !!synchronous);
        modifySession.subscribe(() => {
            this.log.log("closed training session");
            this.persistenceService.removeItem("trainingSessionId").subscribe();
            this.persistenceService.removeItem("learnerId").subscribe();
            this.eventBus.next(new SessionEvent(SESSION_STATUS.close_trainingsession));
        }, () => {
            this.log.log("could not close training session");
        });
        return modifySession;
    }

    /*
     * Begin a new reading passage (begins a new book session on server-side)
     */
    public startBookSession(selection: ILibrarySelection, type: BookSessionType):Observable<Object> {
        let sessionData = this.createStepChangePayload(selection.storyDescription.referenceStory, type);
        let url = this.generateStartBookSessionUrl();
        return Observable.create((observer) => {
            // send session data to the server
            this.closeBookSession().subscribe(() => {
                this.uploadService.startBookSession(sessionData, url)
                    .subscribe((result: BookSessionResult) => {
                        this.log.log("started book session: " + selection.storyDescription.referenceStory);
                        this._partType = type;
                        this.setBookSessionId(result);
                        this.eventBus.next(new SessionEvent(SESSION_STATUS.start_booksession));
                        observer.next(result);
                        observer.complete();
                    });
            });
        });
    }

    /*
     * Change the part type for the same reading passage (begins a new book session on server-side)
     */
    public sendStepChange(type:BookSessionType):Observable<Object> {
        if (this.config.isSuperUserMode) {
            console.log("Modify book session is being ignored as we are in mock server mode");
            return of("is-mock-mode");
        }
        // if we are already in preview mode, no need to set it again.
        if (type === this._partType)  {
            console.log("Modify book session is being ignored as we have already modified to the type", type);
            return of("not-necessary");
        }

        let sessionData = this.createStepChangePayload(this.userModelService.storyUniqName, type);
        let url = this.generateStartBookSessionUrl();

        return Observable.create((observer) => {
            this.closeBookSession().subscribe(() => {
                this.uploadService.startBookSession(sessionData, url).subscribe((result: BookSessionResult) => {
                    this.log.log("modified book session");
                    this._partType = type;
                    this._partId = this.DEFAULT_PART_ID;
                    this.setBookSessionId(result);
                    this.eventBus.next(new SessionEvent(SESSION_STATUS.modify_booksession));
                    observer.next(result);
                    observer.complete();
                });
            });
        });
    }

    private createStepChangePayload(storyUniqName: string, type: BookSessionType): Object {
        let sessionData = {
            tenantId: this.login.tenantId,
            learnerId: this.login.learnerId,
            httpSessionId: this.login.sessionId,
            storyUniqName,
            partType: type
        };
        return sessionData;
    }

    private generateStartBookSessionUrl():string {
        let url =  this.config.userModelPath + "start";
        return url;
    }

    private setBookSessionId(data: BookSessionResult): void {
        if (data && data.Description === "SUCCESS" && data.BookSessionId) {
            this._bookSessionId = data.BookSessionId;
            this.persistenceService.setItem("_bookSessionId", this._bookSessionId).subscribe();
        }
    }

    private removeBookSessionId(): void {
        this._bookSessionId = undefined;
        this.persistenceService.removeItem("_bookSessionId").subscribe();
    }

    public getBookSessionId(): string {
        return this._bookSessionId;
    }

    public closeBookSession(synchronous?: boolean):Observable<Object> {

        let modifySession:Observable<Object>;

        if (this._bookSessionId) {
            modifySession = this.uploadService.closeBookSession(
                this.config.trainingPath + this.login.sessionId + "/BookSessions/" +
                this._bookSessionId,
                !!synchronous);
            modifySession.subscribe(() => {
                this.log.log("closed book session with id " + this._bookSessionId);
                this.removeBookSessionId();
                this._partType = undefined;
                this._partId = undefined;
                this.eventBus.next(new SessionEvent(SESSION_STATUS.close_booksession));
            });
        } else {
            modifySession = of("OK");
            modifySession.subscribe(() => this.log.log("No book session to close"));
        }
        return modifySession;
    }

    public sendBookSessionData(data:{}, successCallback?:Function, failureCallback?:Function):Observable<string> {

        let bookSession = Object.assign({"bookSessionId": this.getBookSessionId()}, data);
        let bookSessionData = {
                "bookSessionList": [bookSession],
                "httpSessionId": this.login.sessionId
            };
        return this.uploadService.modifyBookSession(bookSessionData, true, successCallback, failureCallback);

    }

    public get partId(): string {
        return this.DEFAULT_PART_ID;
    }

    public get currentPartType(): BookSessionType {
        return this._partType;
    }

    public exit(userClosedWindow: boolean = false):Observable<string> {
        const userClosedChrome = userClosedWindow && this.browserAgent.getBrowserName().startsWith(("Chrome"));
        if (!this.exitProcessTriggered) {
            this.exitProcessTriggered = true;
            return Observable.create((observer:Observer<any>) => {
                if (this.caniOsExit()) {
                    this.uploadService.clearFailureState();
                    this.closeBookSession(true);
                    this.clientSideSessionCleanup();
                    this.eventBus.next(new ExitEvent(EXIT_STATUS.ok));
                } else if (userClosedChrome) {
                    // If user closes the browser window in Chrome: due to
                    // https://carnegielearning.zoom.us/j/9807157384?pwd=R0JYUlZ1KzV6V2dmTkdycVdnOCtRZz09&from=addon
                    // clean exit will not occur if close is called synchronously.  We call the training session close;
                    // slc cleanup will close the booksession as well
                    this.closeTrainingSession(false);
                    this.clientSideSessionCleanup();
                } else {
                    this.uploadService.clearFailureState();
                    try {
                        this.closeBookSession(true).pipe(flatMap((result) => {
                            return forkJoin([
                                of(result),
                                this.closeTrainingSession(true)
                            ]);
                        })).subscribe(() => {
                            this.clientSideSessionCleanup();
                            observer.next("OK");
                            observer.complete();
                            this.log.log("exit completed");
                        }, () => {
                            this.clientSideSessionCleanup();
                            observer.next("OK");
                            observer.complete();
                            this.log.error("exit completed with observable error(s)");
                        });
                    } catch (e) {
                        this.clientSideSessionCleanup();
                        observer.error("Error on exit: " + e);
                        observer.complete();
                        this.log.error("exit completed caught error(s)" + e);
                    }
                }
            });
        } else {
            return empty();
        }
    }

    private caniOsExit():boolean {
        return this.browserAgent.getBrowserName() === 'Safari' &&
               (this._currentRetries >= SessionService.MAX_RETRIES || !this.uploadService.allowUpload);
    }

    private setKeepAlive():void {
        if (!this.keepAliveSubscription) {
            this.log.log("Setting keepAlive interval");
            this.keepAliveSubscription = interval(this.config.getKeepAliveInterval()).subscribe(
                () => this.keepAlive());
        }
    }

    private keepAlive():void {
        let modifySession: Observable<Object> = this.uploadService.keepAlive(
            this.config.keepAlivePath + this.login.sessionId);

        modifySession.subscribe((reason) => {
            this.log.log("KeepAlive");
            this._currentRetries = 0;
        }, (result) => {
            this._currentRetries++;
            if (this._currentRetries >= SessionService.MAX_RETRIES) {
                this.log.error("KeepAlive failed after " + SessionService.MAX_RETRIES + " retries.  Reason: " + result.name,
                    ErrorType.CONNECTION);
                this.discontinueKeepAlive();
                this.modalConfig.openMessageModal("exit_while_offline");
            }
        });
    }

    private discontinueKeepAlive():void {
        if (this.keepAliveSubscription) {
            this.log.log("Removing keepAlive interval");
            this._currentRetries = 0;
            this.keepAliveSubscription.unsubscribe();
            this.keepAliveSubscription = undefined;
        }
    }

    private clientSideSessionCleanup(): void {
        this.discontinueKeepAlive();
        this._bookSessionId = undefined;
        this._currentRetries = 0;
    }

    public get completedGesture(): boolean {
        return this._completedGesture;
    }

    public setCompletedGesture(completedGesture: boolean): void {
        this._completedGesture = completedGesture;
    }
}
